import { Typography } from '@ace-clublabs/shared-component-library';
import {
  ManageMembershipItem,
  ManageMembershipItemAlert,
  ManageMembershipItemButton,
  ManageMembershipItemInfo,
} from '../ManageMembershipItem/ManageMembershipItem';
import { setGlobalState, useGlobalState } from '../../../state/state';
import { getRVProductRule, getRVWillBeIncluded } from '../../../utils/ruleHelper';
import { MSSProduct, MSSProductStatus } from '@lib/types';
import { Pages } from '../../../utils/constants';
import { useHistory } from 'react-router-dom';
import { MembershipLevel, MembershipProductType } from '@ace-clublabs/common-membership-api-types';

export const RVMotorcycle = () => {
  const history = useHistory();
  const [membershipDetails] = useGlobalState('memberDetails');
  const [formData] = useGlobalState('formData');
  const currentBenefitLevel = formData.requestedMembershipLevel;

  const rvRule = getRVProductRule(membershipDetails, currentBenefitLevel!);
  const rvFormProduct = formData.products?.find((product) => product.type === MembershipProductType.RV);
  const rvProduct = membershipDetails.products?.find((product) => product.type === MembershipProductType.RV);

  const rvWillBeIncluded = getRVWillBeIncluded(
    membershipDetails,
    formData.requestedMembershipLevel || currentBenefitLevel!
  );
  //show RV alert if customer is in renewal & in TX or NM & not classic
  const isInRenewal = membershipDetails.inRenewal;
  const isTXorNM = membershipDetails?.clubCode === '252' || membershipDetails?.clubCode === '601';
  const isNotClassic = formData.requestedMembershipLevel !== MembershipLevel.CLASSIC;

  const isRVRemoved = rvFormProduct?.status === MSSProductStatus.REMOVED;
  const isRVAdded = rvFormProduct?.status === MSSProductStatus.ADDED;
  const isRVExisting = rvProduct?.status === MSSProductStatus.EXISTING;
  const isRVIncluded = rvProduct?.status === MSSProductStatus.INCLUDED || rvWillBeIncluded;

  const downgradesAllowed = membershipDetails.prospectiveMemberRules.downgradesAllowed;
  const membershipCanAddRV =
    membershipDetails.prospectiveMemberRules.upgradesAllowed && rvRule === MSSProductStatus.ALLOWED;
  const renderRV = membershipCanAddRV || isRVIncluded;

  const removeRVMotorcycle = () => {
    let products: MSSProduct[] | undefined = formData.products;

    if (isRVAdded) {
      products = formData?.products?.filter((product) => product.type !== MembershipProductType.RV);
    } else if (isRVExisting) {
      products = formData?.products?.map((product) =>
        product.type === MembershipProductType.RV
          ? {
              ...product,
              status: MSSProductStatus.REMOVED,
            }
          : product
      );
    }

    setGlobalState('formData', {
      ...formData,
      products,
    });
  };

  const getRVMotorcycleItems = (): (
    | ManageMembershipItemInfo
    | ManageMembershipItemButton
    | ManageMembershipItemAlert
  )[] => {
    const canRemoveRV = (downgradesAllowed && isRVExisting) || isRVAdded;

    const addedRVItem = {
      type: 'info',
      title: 'Added',
      subtitle: isRVAdded ? (
        <Typography variant="b3Regular" id="rv-effect-date-message">
          Changes <strong>will be effective seven (7) days</strong> after full payment is received.
        </Typography>
      ) : (
        <></>
      ),
      className: 'rv-motorcycle',
      key: 'rv-motorcycle-added',
      rightIconName: canRemoveRV ? 'xmark-regular' : undefined,
      rightIconOnClick: removeRVMotorcycle,
    } as ManageMembershipItemInfo;

    const includedRVItem = {
      type: 'info',
      title: 'Included',
      className: 'rv-motorcycle',
      key: 'rv-motorcycle-included',
    } as ManageMembershipItemInfo;

    const RVAlert = {
      type: 'alert',
      severity: 'warning',
      displayCloseButton: false,
      text: (
        <Typography variant="b2Regular">
          <strong>Roadside Assistance for RV/motorcycle</strong> is no longer included with Plus<sup>®</sup> and Premier
          <sup>®</sup>. You can add it as a separate benefit.
        </Typography>
      ),
      iconName: 'bullhorn-regular',
      className: 'rv-motorcycle-alert',
      key: 'rv-motorcycle-alert',
    } as ManageMembershipItemAlert;

    const addRVButton = {
      type: 'button',
      text: 'Add',
      onClick: () => {
        history.push(Pages.AddRVMotorcycle);
      },
      iconName: 'plus-regular',
      className: `rv-motorcycle`,
      key: 'rv-motorcycle-add-btn',
    } as ManageMembershipItemButton;

    if (isRVRemoved) {
      return [addRVButton];
    } else if (isRVAdded || isRVExisting) {
      return [addedRVItem];
    } else if (isRVIncluded) {
      return [includedRVItem];
    } else if (isInRenewal && isTXorNM && isNotClassic) {
      return [RVAlert, addRVButton];
    } else {
      return [addRVButton];
    }
  };

  return (
    <>
      {renderRV ? (
        <ManageMembershipItem
          sectionTitle="Roadside for RV/motorcycle"
          iconName="van-shuttle-duotone"
          titleClassName="rv-motorcycle"
          items={getRVMotorcycleItems()}
        />
      ) : (
        <></>
      )}
    </>
  );
};
